import { Loader2 } from "lucide-react";
import { cn } from "@headerhawk/ui";

/**
 * The dashboard's one loading affordance.
 *
 * Before task 169 there were fourteen hand-rolled copies in four looks: a
 * muted spinner, a primary-coloured spinner, a pulsing globe, and the bare
 * words "Loading...". The spinner is the one kept, because a static line of
 * text reads as a page that has finished and has nothing on it, and a globe
 * says "sites" rather than "waiting".
 *
 * `FullScreenLoader` is the page gate: it is the entire page while auth,
 * provisioning or a route chunk is in flight. `SectionLoader` is the same
 * spinner inside a page that has already drawn its own frame.
 *
 * The spinner itself is `aria-hidden`; the loading state is announced through
 * `role="status"` and either the visible label or a screen-reader-only
 * "Loading...".
 */

interface LoaderProps {
  /**
   * Drawn under the spinner. Pass one only where it says something the
   * spinner does not ("Confirming your subscription..."); otherwise the
   * state is announced but not written out.
   */
  label?: string;
}

function Spinner({ label }: LoaderProps) {
  return (
    <>
      <Loader2
        className="h-8 w-8 animate-spin text-muted-foreground"
        aria-hidden="true"
      />
      {label ? (
        <p className="text-muted-foreground">{label}</p>
      ) : (
        <span className="sr-only">Loading...</span>
      )}
    </>
  );
}

/** Fills the viewport. For gates that render instead of the page. */
export function FullScreenLoader({ label }: LoaderProps) {
  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center gap-4"
      role="status"
    >
      <Spinner label={label} />
    </div>
  );
}

interface SectionLoaderProps extends LoaderProps {
  /** Override the default vertical padding where a page needs more or less. */
  className?: string;
}

/** Fills the space a page has already laid out for its content. */
export function SectionLoader({ label, className }: SectionLoaderProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-4 py-12",
        className,
      )}
      role="status"
    >
      <Spinner label={label} />
    </div>
  );
}
